import { SUBSPLASH_AUTH_PROVIDER_ID } from '@omni/kit/constants/identifiers';
import BaseService, { IHttpResponse } from '@omni/kit/services/BaseService';
import pluralize from 'pluralize';

import { getSystemState } from './storeAccessor';

const debug = require('debug')('omni:messaging:service:ChatService');

export default class ChatService {
  private static get host() {
    return getSystemState().environment;
  }

  private static get appKey() {
    return getSystemState().appKey;
  }

  private static get token() {
    return getSystemState().accessToken;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static getAll(resource: string) {
    const url = this._buildUrl(resource);

    return BaseService.Get({
      url: url,
      appKey: this.appKey,
      authProviderId: SUBSPLASH_AUTH_PROVIDER_ID,
      token: this.token,
    })
      .then(this._handleAllResponse)
      .catch(this._handleErrorResponse);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static getFiltered(
    resource: string,
    filters: [string, string][],
    appKey?: string,
    accessToken?: string
  ) {
    const token = accessToken || this.token;

    if (!token) {
      return Promise.reject({
        status: 401,
        message: 'Unauthorized: no token provided',
      });
    }

    const url = this._buildUrl(resource, undefined, filters);

    return BaseService.Get({
      url: url,
      appKey: appKey || this.appKey,
      authProviderId: SUBSPLASH_AUTH_PROVIDER_ID,
      token: token,
    })
      .then(this._handleAllResponse)
      .catch(this._handleErrorResponse);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static getOne(resource: string, id: string, filters?: [string, string][]) {
    const url = this._buildUrl(resource, id, filters);

    return BaseService.Get({
      url: url,
      appKey: this.appKey,
      authProviderId: SUBSPLASH_AUTH_PROVIDER_ID,
      token: this.token,
    })
      .then(this._handleOneResponse)
      .catch(this._handleErrorResponse);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static patch(
    resource: string,
    id: string,
    data: object,
    accessToken?: string
  ) {
    const url = this._buildUrl(resource, id);
    debug('url', url);

    return BaseService.Patch({
      url: url,
      appKey: this.appKey,
      authProviderId: SUBSPLASH_AUTH_PROVIDER_ID,
      token: accessToken || this.token,
      data: data,
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(this._handleOneResponse)
      .catch(this._handleErrorResponse);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static post(resource: string, data: object, id = '') {
    const url = this._buildUrl(resource, id);

    return BaseService.Post({
      url: url,
      appKey: this.appKey,
      authProviderId: SUBSPLASH_AUTH_PROVIDER_ID,
      token: this.token,
      data: data,
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(this._handleOneResponse)
      .catch(this._handleErrorResponse);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  static delete(resource: string, id: string) {
    const url = this._buildUrl(resource, id);

    return BaseService.Delete({
      url: url,
      appKey: this.appKey,
      authProviderId: SUBSPLASH_AUTH_PROVIDER_ID,
      token: this.token,
    })
      .then(this._handleOneResponse)
      .catch(this._handleErrorResponse);
  }

  private static _buildUrl(
    resource: string,
    id?: string,
    filters?: [string, string][]
  ) {
    id = id ? `/${id}` : '';

    let filtersString = '';

    if (filters) {
      let prefix = '?';
      filters.forEach((filter) => {
        filtersString =
          filtersString + `${prefix}filter[${filter[0]}]=${filter[1]}`;
        prefix = '&';
      });
    }

    return `${this.host}/${pluralize(resource)}${id}${filtersString}`;
  }

  private static _handleOneResponse(response: IHttpResponse): {
    meta: { count?: number; total?: number };
    status: number;
    message?: string;
    data?: any;
  } {
    const ok =
      response.status === 200 ||
      response.status === 201 ||
      response.status === 204;

    if (!ok) throw response;

    return {
      meta: {},
      status: response.status || 0,
      data: response.body,
    };
  }

  private static _handleAllResponse(response: IHttpResponse): {
    meta: { count?: number; total?: number };
    status: number;
    data?: any;
  } {
    const ok =
      response.status === 200 ||
      response.status === 201 ||
      response.status === 204;

    if (!ok) throw response;

    return {
      meta: {
        count: response.body.count,
        total: response.body.total,
      },
      status: response.status || 0,
      data: response.body._embedded,
    };
  }

  private static _handleErrorResponse(response: IHttpResponse): {
    status: number;
    message: string;
    data?: any;
  } {
    return {
      status: response.status || 0,
      message: Array.isArray(response.body?.errors)
        ? response.body.errors[0].detail
        : Array.isArray(response.errors)
        ? response.errors[0].detail || response.message
        : JSON.stringify(response.body),
    };
  }
}
